import { css, cx, keyframes } from '@emotion/css';
import { useEffect, useState, useRef, type HTMLAttributes } from 'react';
import { toast } from 'react-toastify';
import { useGetOrderSession, useGetScheduleMapSvg, useGetSchedule, useGetScheduleSeats } from '../../hooks/checkout-api.hooks';
import PinchZoom from '../../../../shared/components/pinch-zoom';
import type { SeatAvailability } from '../../../../api/services/events/types';

const container = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-lg)',
  width: '100%',
  height: '100%',
});

const title = css({
  fontSize: 'var(--fs-500)',
  fontWeight: 600,
  color: 'var(--pc-800)',
});

const mapWrapper = css({
  position: 'relative',
  width: 'calc(100% + calc(var(--spacing-xl) * 2))',
  marginLeft: 'calc(var(--spacing-xl) * -1)',
  height: '600px',
  minHeight: '400px',
  backgroundColor: 'var(--g-50)',
  borderRadius: 'var(--spacing-md)',
  border: '1px solid var(--g-200)',
  overflow: 'hidden',
  '@media (max-width: 768px)': {
    width: 'calc(100% + calc(var(--spacing-lg) * 2))',
    marginLeft: 'calc(var(--spacing-lg) * -1)',
    height: '500px',
  },
});

const svgContainer = css({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    maxWidth: '100%',
    maxHeight: '100%',
    height: 'auto',
    width: 'auto',
  },
});

const loadingMessage = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: 'var(--g-600)',
  fontSize: 'var(--fs-400)',
});

const errorMessage = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: 'var(--error)',
  fontSize: 'var(--fs-400)',
});

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const loadingOverlay = css({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  color: 'white',
});

const spinner = css({
  border: '4px solid rgba(255, 255, 255, 0.3)',
  borderTop: '4px solid white',
  borderRadius: '50%',
  width: '50px',
  height: '50px',
  animation: `${spin} 1s linear infinite`,
});

const legendOverlay = css({
  position: 'absolute',
  bottom: '16px',
  left: '16px',
  zIndex: 10,
  '@media (max-width: 768px)': {
    bottom: '12px',
    left: '12px',
  },
});

const legendContainer = css({
  width: '180px',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderRadius: '4px',
  border: '1px solid rgba(0, 0, 0, 0.15)',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  '@media (max-width: 768px)': {
    width: '160px',
  },
});

const legendHeader = css({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '6px 10px',
  background: 'rgba(255, 255, 255, 0.95)',
  border: 'none',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 600,
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
  },
});

const legendChevron = css({
  transition: 'transform 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  '&.open': {
    transform: 'rotate(180deg)',
  },
});

const legendContent = css({
  padding: 0,
  maxHeight: 0,
  overflow: 'hidden',
  transition: 'max-height 0.3s ease, padding 0.3s ease',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  '&.open': {
    maxHeight: '200px',
    padding: '0 8px 8px',
  },
});

const legendItems = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const legendItem = css({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 0',
});

const legendIcon = css({
  width: '24px',
  height: '24px',
  flexShrink: 0,
});

const legendLabel = css({
  fontSize: '14px',
  fontWeight: 500,
});

const modalOverlay = css({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
  padding: '16px',
  backdropFilter: 'blur(4px)',
});

const modalContent = css({
  background: 'white',
  borderRadius: '12px',
  padding: '24px',
  maxWidth: '500px',
  width: '100%',
  maxHeight: '80vh',
  overflowY: 'auto',
  position: 'relative',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
  animation: 'modalSlideIn 0.3s ease-out',
  '@keyframes modalSlideIn': {
    from: {
      opacity: 0,
      transform: 'translateY(-20px) scale(0.95)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
  },
  '@media (max-width: 768px)': {
    padding: '20px',
    maxWidth: '90%',
  },
  '@media (max-width: 480px)': {
    padding: '16px',
    maxWidth: '95%',
  },
});

const modalHeader = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
  paddingBottom: '16px',
  borderBottom: '2px solid var(--g-200)',
});

const modalTitle = css({
  fontSize: '20px',
  fontWeight: 600,
  color: 'var(--g-900)',
  margin: 0,
  '@media (max-width: 768px)': {
    fontSize: '18px',
  },
  '@media (max-width: 480px)': {
    fontSize: '16px',
  },
});

const modalClose = css({
  background: 'none',
  border: 'none',
  fontSize: '28px',
  cursor: 'pointer',
  color: 'var(--g-600)',
  padding: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s',
  lineHeight: 1,
  width: '32px',
  height: '32px',
  borderRadius: '4px',
  '&:hover': {
    color: 'var(--g-900)',
    background: 'var(--g-100)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
});

const seatInfo = css({
  background: 'var(--g-100)',
  padding: '16px',
  borderRadius: '8px',
  marginBottom: '20px',
  border: '1px solid var(--g-200)',
  '& p': {
    margin: '6px 0',
    color: 'var(--g-700)',
    fontSize: '14px',
    lineHeight: '1.5',
    '&:first-of-type': {
      marginTop: 0,
    },
    '&:last-of-type': {
      marginBottom: 0,
    },
    '& strong': {
      color: 'var(--g-900)',
      fontWeight: 600,
    },
  },
});

const ticketList = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

const ticketOption = css({
  border: '1px solid rgba(var(--pcrgba-200), 0.5)',
  borderRadius: '8px',
  padding: '16px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  background: 'white',
  '&:hover': {
    backgroundColor: 'rgba(var(--pcrgba-200), 0.1)',
    borderColor: 'rgba(var(--pcrgba-300), 0.7)',
  },
  '@media (max-width: 520px)': {
    padding: '12px',
  },
});

const ticketOptionHeader = css({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '8px',
});

const ticketRadio = css({
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  border: '2px solid rgba(var(--pcrgba-400), 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: '12px',
  flexShrink: 0,
});

const ticketRadioInner = css({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: 'transparent',
  transition: 'background-color 0.2s ease',
});

const ticketRadioInnerSelected = css({
  backgroundColor: 'var(--pc-600)',
});

const ticketName = css({
  fontSize: '16px',
  fontWeight: 600,
  color: 'var(--pc-800)',
  margin: 0,
  flexGrow: 1,
  '@media (max-width: 520px)': {
    fontSize: '15px',
  },
});

const ticketPrice = css({
  fontSize: '16px',
  fontWeight: 600,
  color: 'var(--pc-700)',
  marginLeft: 'auto',
  '@media (max-width: 520px)': {
    fontSize: '15px',
  },
});

const ticketDescription = css({
  fontSize: '14px',
  color: 'var(--pc-600)',
  margin: '0 0 0 32px',
  lineHeight: '1.5',
  '@media (max-width: 520px)': {
    fontSize: '13px',
    marginLeft: '30px',
  },
});

const loadingTicketsStyle = css({
  textAlign: 'center',
  padding: '40px 20px',
  color: 'var(--g-600)',
  '& p': {
    margin: '12px 0 0',
    fontSize: '14px',
  },
});

const noTicketsStyle = css({
  textAlign: 'center',
  padding: '40px 20px',
  color: 'var(--g-500)',
  '& p': {
    margin: 0,
    fontSize: '14px',
    lineHeight: '1.6',
  },
});

interface Seat {
  zone: string;
  row: string;
  column: string;
}

interface TicketType {
  name: string;
  price: number;
  seatsPerTicket?: number;
}

interface SelectedSeatWithTicket extends Seat {
  ticketType: string;
  price: number;
  seatsPerTicket?: number;
  multiGroupId?: string;
}

export interface CheckoutSeatsProps extends HTMLAttributes<HTMLDivElement> {
  sessionId: string;
  onSeatsChange?: (seats: SelectedSeatWithTicket[]) => void;
}

const SELECTED_COLOR = '#9302CF';
const OPEN_COLOR = '#277A00';
const UNAVAILABLE_COLOR = '#ADCDE0';
const RESERVED_COLOR = '#FFA500'; // Orange for reserved seats

const ChevronDownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const SeatLegend = () => {
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth > 768;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      setIsOpen(window.innerWidth > 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={legendContainer}>
      <button className={legendHeader} onClick={() => setIsOpen(prev => !prev)}>
        <span>Legenda</span>
        <div className={cx(legendChevron, { open: isOpen })}>
          <ChevronDownIcon />
        </div>
      </button>
      
      <div className={cx(legendContent, { open: isOpen })}>
        <div className={legendItems}>
          <div className={legendItem}>
            <svg width="24" height="24" viewBox="0 0 91 92" fill={OPEN_COLOR} className={legendIcon}>
              <path d="M0 78.9349C0 73.8238 4.12271 69.6805 9.20833 69.6805H81.7917C86.8773 69.6805 91 73.8238 91 78.9349V82.7456C91 87.8566 86.8773 92 81.7917 92H9.20833C4.12271 92 0 87.8566 0 82.7456V78.9349Z" />
              <path d="M8.125 18.5089C8.125 8.28671 16.3704 0 26.5417 0H65C75.1712 0 83.4167 8.28671 83.4167 18.5089V63.1479H8.125V18.5089Z" />
            </svg>
            <span className={legendLabel}>Disponível</span>
          </div>
          <div className={legendItem}>
            <svg width="24" height="24" viewBox="0 0 91 92" fill={SELECTED_COLOR} className={legendIcon}>
              <path d="M0 78.9349C0 73.8238 4.12271 69.6805 9.20833 69.6805H81.7917C86.8773 69.6805 91 73.8238 91 78.9349V82.7456C91 87.8566 86.8773 92 81.7917 92H9.20833C4.12271 92 0 87.8566 0 82.7456V78.9349Z" />
              <path d="M8.125 18.5089C8.125 8.28671 16.3704 0 26.5417 0H65C75.1712 0 83.4167 8.28671 83.4167 18.5089V63.1479H8.125V18.5089Z" />
            </svg>
            <span className={legendLabel}>Selecionado</span>
          </div>
          <div className={legendItem}>
            <svg width="24" height="24" viewBox="0 0 91 92" fill={RESERVED_COLOR} className={legendIcon}>
              <path d="M0 78.9349C0 73.8238 4.12271 69.6805 9.20833 69.6805H81.7917C86.8773 69.6805 91 73.8238 91 78.9349V82.7456C91 87.8566 86.8773 92 81.7917 92H9.20833C4.12271 92 0 87.8566 0 82.7456V78.9349Z" />
              <path d="M8.125 18.5089C8.125 8.28671 16.3704 0 26.5417 0H65C75.1712 0 83.4167 8.28671 83.4167 18.5089V63.1479H8.125V18.5089Z" />
            </svg>
            <span className={legendLabel}>Reservado</span>
          </div>
          <div className={legendItem}>
            <svg width="24" height="24" viewBox="0 0 91 92" fill={UNAVAILABLE_COLOR} className={legendIcon}>
              <path d="M0 78.9349C0 73.8238 4.12271 69.6805 9.20833 69.6805H81.7917C86.8773 69.6805 91 73.8238 91 78.9349V82.7456C91 87.8566 86.8773 92 81.7917 92H9.20833C4.12271 92 0 87.8566 0 82.7456V78.9349Z" />
              <path d="M8.125 18.5089C8.125 8.28671 16.3704 0 26.5417 0H65C75.1712 0 83.4167 8.28671 83.4167 18.5089V63.1479H8.125V18.5089Z" />
            </svg>
            <span className={legendLabel}>Indisponível</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export function CheckoutSeats({
  sessionId,
  onSeatsChange,
  className,
  style,
  ...props
}: CheckoutSeatsProps) {
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [clickedSeats, setClickedSeats] = useState<Set<string>>(new Set());
  const [selectedSeatsWithTickets, setSelectedSeatsWithTickets] = useState<Map<string, SelectedSeatWithTicket>>(new Map());
  const [forceOpenSeats, setForceOpenSeats] = useState<Set<string>>(new Set());
  const [isOverSeat, setIsOverSeat] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentSeat, setCurrentSeat] = useState<Seat | null>(null);
  const [availableTickets, setAvailableTickets] = useState<TicketType[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [svgProcessing, setSvgProcessing] = useState(false);
  const [selectedTicketIndex, setSelectedTicketIndex] = useState<number | null>(null);
  const [pendingMultiTicket, setPendingMultiTicket] = useState<{
    ticketType: string;
    price: number;
    seatsPerTicket: number;
    zone: string;
    groupId: string;
    selectedCount: number;
  } | null>(null);

  // Get session data to extract scheduleId
  const { data: sessionData } = useGetOrderSession(sessionId, !!sessionId);
  
  const scheduleId = sessionData?.session?.metadata?.scheduleId || '';

  // Fetch the SVG map
  const { data: svgContent, isLoading, isError } = useGetScheduleMapSvg(
    scheduleId,
    !!scheduleId
  );

  // Fetch schedule details
  const { data: scheduleData } = useGetSchedule(scheduleId, !!scheduleId);

  // Fetch seats availability data
  const { data: seatsData } = useGetScheduleSeats(scheduleId, !!scheduleId);

  // Notify parent of seat changes with ticket types
  useEffect(() => {
    const seatsArray = Array.from(selectedSeatsWithTickets.values());
    onSeatsChange?.(seatsArray);
  }, [selectedSeatsWithTickets, onSeatsChange]);

  // Helper function to get seat color based on availability
  const getSeatColor = (zone: string, row: string, column: string): string => {
    if (!seatsData?.seats) return OPEN_COLOR;

    // Find the seat in the seats data
    const seat = seatsData.seats.find(
      (s) =>
        s.zone.toLowerCase() === zone.toLowerCase() &&
        s.row.toLowerCase() === row.toLowerCase() &&
        s.column.toLowerCase() === column.toLowerCase()
    );

    if (!seat) return OPEN_COLOR;

    // Map availability to color
    switch (seat.availability as SeatAvailability) {
      case 'available':
        return OPEN_COLOR;
      case 'reserved':
        return RESERVED_COLOR;
      case 'sold':
        return UNAVAILABLE_COLOR;
      default:
        return OPEN_COLOR;
    }
  };

  // Helper function to check if a seat belongs to the current session
  const seatBelongsToSession = (zone: string, row: string, column: string): boolean => {
    const items = sessionData?.session?.items || [];
    for (const item of items) {
      const seats = item?.seats || [];
      if (
        seats.some(
          (s: any) =>
            (s.zone || '').toLowerCase() === zone.toLowerCase() &&
            (s.row || '').toLowerCase() === row.toLowerCase() &&
            (s.column || '').toLowerCase() === column.toLowerCase()
        )
      ) {
        return true;
      }
    }
    return false;
  };

  // Helper function to check if seat is available for booking
  const isSeatAvailable = (zone: string, row: string, column: string): boolean => {
    // If user explicitly forced this seat open (deselected from own session), allow selecting again
    const seatKey = `${zone}|${row}|${column}`;
    if (forceOpenSeats.has(seatKey)) return true;

    if (!seatsData?.seats) return true;

    const seat = seatsData.seats.find(
      (s) =>
        s.zone.toLowerCase() === zone.toLowerCase() &&
        s.row.toLowerCase() === row.toLowerCase() &&
        s.column.toLowerCase() === column.toLowerCase()
    );

    if (!seat) return true;
    if (seat.availability === 'available') return true;
    if (seat.availability === 'reserved') {
      // If reserved but belongs to this session, allow selecting it
      return seatBelongsToSession(zone, row, column);
    }
    // sold or any other state
    return false;
  };

  // Helper function to color seat elements
  const colorSeatElement = (seatElement: Element, fill: string) => {
    const children = seatElement.querySelectorAll('*');
    children.forEach((el) => {
      el.setAttribute('fill', fill);
    });
    if (
      seatElement instanceof SVGElement &&
      seatElement.tagName.toLowerCase() !== 'g' &&
      seatElement.hasAttribute('fill')
    ) {
      seatElement.setAttribute('fill', fill);
    }
  };

  // Helper function to find seat at specific point
  const findSeatAtPoint = (clientX: number, clientY: number, scope: HTMLElement): Element | null => {
    if (typeof document === 'undefined') return null;
    const elements = document.elementsFromPoint(clientX, clientY) as Element[];
    for (const el of elements) {
      if (!scope.contains(el)) continue;
      let current: Element | null = el;
      while (current && current !== scope) {
        if (current.hasAttribute && current.hasAttribute('anchor-seat')) {
          return current;
        }
        current = current.parentElement;
      }
    }
    return null;
  };

  // Mouse move handler
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!mapContainerRef.current) return;
    const container = mapContainerRef.current;

    let target: Element | null = e.target as Element | null;
    let seatElement: Element | null = null;
    
    while (target && target !== container) {
      if (target.hasAttribute && target.hasAttribute('anchor-seat')) {
        seatElement = target;
        break;
      }
      target = target.parentElement;
    }

    if (!seatElement) {
      seatElement = findSeatAtPoint(e.clientX, e.clientY, container);
    }

    setIsOverSeat(!!seatElement);
  };

  // Seat click handler
  const handleSeatClick = async (e: React.MouseEvent<HTMLDivElement>) => {
    if (!mapContainerRef.current) return;
    const container = mapContainerRef.current;

    let target: Element | null = e.target as Element | null;
    let seatElement: Element | null = null;
    while (target && target !== container) {
      if (target.hasAttribute && target.hasAttribute('anchor-seat')) {
        seatElement = target;
        break;
      }
      target = target.parentElement;
    }

    if (!seatElement) {
      seatElement = findSeatAtPoint(e.clientX, e.clientY, container);
      if (!seatElement) return;
    }

    const zone =
      seatElement.getAttribute('anchor-seat-zone') ||
      seatElement.getAttribute('anchor-zone') ||
      '';
    const row =
      seatElement.getAttribute('anchor-seat-row') ||
      seatElement.getAttribute('anchor-row') ||
      '';
    const column =
      seatElement.getAttribute('anchor-seat-column') ||
      seatElement.getAttribute('anchor-column') ||
      '';
    const seatKey = `${zone}|${row}|${column}`;

    const isCurrentlyClicked = clickedSeats.has(seatKey);

    if (isCurrentlyClicked) {
      // Deselect seat - remove from clicked seats and ticket mapping
      setClickedSeats((prev) => {
        const newSet = new Set(prev);
        newSet.delete(seatKey);
        return newSet;
      });
      setSelectedSeatsWithTickets((prev) => {
        const newMap = new Map(prev);
        const removed = newMap.get(seatKey);
        // If this seat belonged to an ongoing multi-ticket, decrement its counter
        if (removed?.multiGroupId && pendingMultiTicket?.groupId === removed.multiGroupId) {
          setPendingMultiTicket({
            ...pendingMultiTicket,
            selectedCount: Math.max(0, pendingMultiTicket.selectedCount - 1),
          });
        }
        newMap.delete(seatKey);
        return newMap;
      });
      // Remember this seat should remain green despite API reserved state
      setForceOpenSeats((prev) => {
        const next = new Set(prev);
        next.add(seatKey);
        return next;
      });
      // Turn seat back to available (green)
      colorSeatElement(seatElement, OPEN_COLOR);
      
      // Show info toast
      toast.info(`ℹ️ Assento ${row}${column} da zona ${zone} removido`, {
        position: "top-left",
        autoClose: 2000,
      });
    } else {
      // If we have a pending multi-seat ticket selection, auto-assign without opening modal
      if (pendingMultiTicket && pendingMultiTicket.selectedCount < pendingMultiTicket.seatsPerTicket) {
        // Enforce same zone
        if (zone.toLowerCase() !== pendingMultiTicket.zone.toLowerCase()) {
          toast.error(`O ingresso múltiplo é válido apenas para a zona ${pendingMultiTicket.zone}.`);
          return;
        }
        // Check availability
        if (!isSeatAvailable(zone, row, column)) {
          toast.error(`😔 Assento ${row}${column} da zona ${zone} não está disponível para reserva`, {
            position: "top-left",
            autoClose: 3000,
          });
          return;
        }
        // Select it with same ticket settings
        setClickedSeats((prev) => {
          const newSet = new Set(prev);
          newSet.add(seatKey);
          return newSet;
        });
        setSelectedSeatsWithTickets((prev) => {
          const newMap = new Map(prev);
          newMap.set(seatKey, {
            zone,
            row,
            column,
            ticketType: pendingMultiTicket.ticketType,
            price: pendingMultiTicket.price,
            seatsPerTicket: pendingMultiTicket.seatsPerTicket,
            multiGroupId: pendingMultiTicket.groupId,
          });
          return newMap;
        });
        // Ensure this seat is no longer marked as force-open; selected should take precedence
        setForceOpenSeats((prev) => {
          const next = new Set(prev);
          next.delete(seatKey);
          return next;
        });
        // Color selected
        const selectorA = `[anchor-seat][anchor-seat-zone="${zone}"][anchor-seat-row="${row}"][anchor-seat-column="${column}"]`;
        const selectorB = `[anchor-seat][anchor-zone="${zone}"][anchor-row="${row}"][anchor-column="${column}"]`;
        const seatEl = mapContainerRef.current?.querySelector(`${selectorA}, ${selectorB}`);
        if (seatEl) colorSeatElement(seatEl, SELECTED_COLOR);

        const newCount = pendingMultiTicket.selectedCount + 1;
        if (newCount >= pendingMultiTicket.seatsPerTicket) {
          setPendingMultiTicket(null);
          toast.success('🎟️ Ingresso múltiplo completo!');
        } else {
          setPendingMultiTicket({ ...pendingMultiTicket, selectedCount: newCount });
          toast.info(`Selecione mais ${pendingMultiTicket.seatsPerTicket - newCount} assento(s) para completar.`);
        }
        return;
      }
      // Check if seat is available before allowing selection
      if (!isSeatAvailable(zone, row, column)) {
        toast.error(`😔 Assento ${row}${column} da zona ${zone} não está disponível para reserva`, {
          position: "top-left",
          autoClose: 3000,
        });
        return;
      }

      // Select seat - open ticket modal
      setCurrentSeat({ zone, row, column });
      setModalOpen(true);
      
      // Fetch available tickets for this zone
      if (scheduleData?.schedule?.prices) {
        setLoadingTickets(true);
        try {
          const zonePrices = scheduleData.schedule.prices.filter(
            (p: any) => p.zone.toLowerCase() === zone.toLowerCase()
          );
          const tickets = zonePrices.map((p: any) => ({
            name: p.name,
            price: parseFloat(p.price),
            seatsPerTicket: p.seatsPerTicket ? Number(p.seatsPerTicket) : 1,
          }));
          setAvailableTickets(tickets);
          
          // Set "Inteira" as default selected
          const inteiraIndex = tickets.findIndex((t: TicketType) => t.name === 'Inteira');
          setSelectedTicketIndex(inteiraIndex >= 0 ? inteiraIndex : 0);
        } catch (error) {
          console.error('Error loading tickets:', error);
          setAvailableTickets([]);
          setSelectedTicketIndex(null);
        } finally {
          setLoadingTickets(false);
        }
      }
    }
  };

  // Handle ticket selection
  const handleTicketSelect = (ticketType: TicketType) => {
    if (!currentSeat) return;

    const { zone, row, column } = currentSeat;
    const seatKey = `${zone}|${row}|${column}`;

    // Add to clicked seats (local state only, no API call)
    setClickedSeats((prev) => {
      const newSet = new Set(prev);
      newSet.add(seatKey);
      return newSet;
    });

    // Store seat with ticket type information
    // Generate a group id if this is a multi-seat ticket
    const groupId = ticketType.seatsPerTicket && ticketType.seatsPerTicket > 1
      ? `${ticketType.name}-${zone}-${Date.now()}`
      : undefined;

    setSelectedSeatsWithTickets((prev) => {
      const newMap = new Map(prev);
      newMap.set(seatKey, {
        zone,
        row,
        column,
        ticketType: ticketType.name,
        price: ticketType.price,
        seatsPerTicket: ticketType.seatsPerTicket,
        multiGroupId: groupId,
      });
      return newMap;
    });

    // Color the seat as selected
    const selectorA = `[anchor-seat][anchor-seat-zone="${zone}"][anchor-seat-row="${row}"][anchor-seat-column="${column}"]`;
    const selectorB = `[anchor-seat][anchor-zone="${zone}"][anchor-row="${row}"][anchor-column="${column}"]`;
    const seatEl = mapContainerRef.current?.querySelector(`${selectorA}, ${selectorB}`);
    if (seatEl) colorSeatElement(seatEl, SELECTED_COLOR);

    // No longer forced-open once selected again
    setForceOpenSeats((prev) => {
      const next = new Set(prev);
      next.delete(seatKey);
      return next;
    });

    // Show success toast
    toast.success(`🎉 Assento ${row}${column} da zona ${zone} selecionado!`, {
      position: "top-left",
      autoClose: 2000,
    });

    // If multi-seat ticket, set pending to avoid modal on next seats
    if (ticketType.seatsPerTicket && ticketType.seatsPerTicket > 1) {
      setPendingMultiTicket({
        ticketType: ticketType.name,
        price: ticketType.price,
        seatsPerTicket: ticketType.seatsPerTicket,
        zone,
        groupId: groupId!,
        selectedCount: 1,
      });
      toast.info(`Selecione mais ${ticketType.seatsPerTicket - 1} assento(s) para completar este ingresso.`);
    } else {
      setPendingMultiTicket(null);
    }

    setModalOpen(false);
  };

  // Pre-select seats that are already reserved/locked in the current session
  useEffect(() => {
    const items = sessionData?.session?.items || [];
    if (!items.length) return;

    // Merge session seats into clicked/selected maps
    setClickedSeats((prev) => {
      const newSet = new Set(prev);
      items.forEach((item: any) => {
        (item.seats || []).forEach((s: any) => {
          const seatKey = `${s.zone}|${s.row}|${s.column}`;
          newSet.add(seatKey);
        });
      });
      return newSet;
    });

    setSelectedSeatsWithTickets((prev) => {
      const newMap = new Map(prev);
      items.forEach((item: any) => {
        const ticketType = item.ticketType as string;
        const unitPrice = parseFloat(item.unitPrice || '0');
        (item.seats || []).forEach((s: any) => {
          const seatKey = `${s.zone}|${s.row}|${s.column}`;
          if (!newMap.has(seatKey)) {
            newMap.set(seatKey, {
              zone: s.zone,
              row: s.row,
              column: s.column,
              ticketType,
              price: unitPrice,
            });
          }
        });
      });
      return newMap;
    });
  }, [sessionData?.session?.items]);

  // Process SVG and add interactivity
  useEffect(() => {
    if (!svgContent || !svgContainerRef.current) return;

    setSvgProcessing(true);

    // Clear previous content
    svgContainerRef.current.innerHTML = '';

    // Parse SVG
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgContent, 'image/svg+xml');
    const svgElement = doc.querySelector('svg');

    if (!svgElement) {
      setSvgProcessing(false);
      return;
    }

    // Find all seats and color them based on availability
    const allSeats = svgElement.querySelectorAll('[anchor-seat]');
    allSeats.forEach((seatElement) => {
      seatElement.setAttribute('style', (seatElement.getAttribute('style') || '') + ' cursor: pointer;');
      
      // Get seat coordinates
      const zone =
        seatElement.getAttribute('anchor-seat-zone') ||
        seatElement.getAttribute('anchor-zone') ||
        '';
      const row =
        seatElement.getAttribute('anchor-seat-row') ||
        seatElement.getAttribute('anchor-row') ||
        '';
      const column =
        seatElement.getAttribute('anchor-seat-column') ||
        seatElement.getAttribute('anchor-column') ||
        '';
      const seatKey = `${zone}|${row}|${column}`;
      
      // Color based on forced-open or availability from API
      if (forceOpenSeats.has(seatKey)) {
        colorSeatElement(seatElement, OPEN_COLOR);
      } else {
        const color = getSeatColor(zone, row, column);
        colorSeatElement(seatElement, color);
      }
    });

    // Make SVG responsive
    svgElement.style.width = '100%';
    svgElement.style.height = '100%';

    // Append to container
    svgContainerRef.current.appendChild(svgElement);

    setTimeout(() => {
      // First, ensure forced-open seats are green
      forceOpenSeats.forEach((seatKey) => {
        const [zone = '', row = '', column = ''] = seatKey.split('|');
        const selectorA = `[anchor-seat][anchor-seat-zone="${zone}"][anchor-seat-row="${row}"][anchor-seat-column="${column}"]`;
        const selectorB = `[anchor-seat][anchor-zone="${zone}"][anchor-row="${row}"][anchor-column="${column}"]`;
        const seatEl = svgContainerRef.current?.querySelector(`${selectorA}, ${selectorB}`);
        if (seatEl) colorSeatElement(seatEl, OPEN_COLOR);
      });
      // Then, re-apply selected seats colors (selected takes precedence over force-open)
      clickedSeats.forEach((seatKey) => {
        const [zone = '', row = '', column = ''] = seatKey.split('|');
        const selectorA = `[anchor-seat][anchor-seat-zone="${zone}"][anchor-seat-row="${row}"][anchor-seat-column="${column}"]`;
        const selectorB = `[anchor-seat][anchor-zone="${zone}"][anchor-row="${row}"][anchor-column="${column}"]`;
        const seatEl = svgContainerRef.current?.querySelector(`${selectorA}, ${selectorB}`);
        if (seatEl) colorSeatElement(seatEl, SELECTED_COLOR);
      });
      setSvgProcessing(false);
    }, 300);
  }, [svgContent, seatsData, clickedSeats, forceOpenSeats]);

  // Update seat colors when seats data changes (real-time availability updates)
  useEffect(() => {
    if (!svgContainerRef.current || !seatsData?.seats) return;

    // Re-color all seats based on updated availability data
    const allSeats = svgContainerRef.current.querySelectorAll('[anchor-seat]');
    allSeats.forEach((seatElement) => {
      const zone =
        seatElement.getAttribute('anchor-seat-zone') ||
        seatElement.getAttribute('anchor-zone') ||
        '';
      const row =
        seatElement.getAttribute('anchor-seat-row') ||
        seatElement.getAttribute('anchor-row') ||
        '';
      const column =
        seatElement.getAttribute('anchor-seat-column') ||
        seatElement.getAttribute('anchor-column') ||
        '';

      const seatKey = `${zone}|${row}|${column}`;
      
      // Don't update color for currently selected seats
      if (clickedSeats.has(seatKey)) {
        colorSeatElement(seatElement, SELECTED_COLOR);
      } else if (forceOpenSeats.has(seatKey)) {
        // Keep forced-open seats green regardless of API state
        colorSeatElement(seatElement, OPEN_COLOR);
      } else {
        const color = getSeatColor(zone, row, column);
        colorSeatElement(seatElement, color);
      }
    });
  }, [seatsData, clickedSeats, forceOpenSeats]);

  // Re-apply colors when clicked seats change
  useEffect(() => {
    if (!svgContainerRef.current) return;
    clickedSeats.forEach((seatKey) => {
      const [zone = '', row = '', column = ''] = seatKey.split('|');
      const selectorA = `[anchor-seat][anchor-seat-zone="${zone}"][anchor-seat-row="${row}"][anchor-seat-column="${column}"]`;
      const selectorB = `[anchor-seat][anchor-zone="${zone}"][anchor-row="${row}"][anchor-column="${column}"]`;
      const seatEl = svgContainerRef.current?.querySelector(`${selectorA}, ${selectorB}`);
      if (seatEl) colorSeatElement(seatEl, SELECTED_COLOR);
    });
  }, [clickedSeats]);

  // Apply/maintain forced-open seats as green
  useEffect(() => {
    if (!svgContainerRef.current) return;
    forceOpenSeats.forEach((seatKey) => {
      const [zone = '', row = '', column = ''] = seatKey.split('|');
      const selectorA = `[anchor-seat][anchor-seat-zone="${zone}"][anchor-seat-row="${row}"][anchor-seat-column="${column}"]`;
      const selectorB = `[anchor-seat][anchor-zone="${zone}"][anchor-row="${row}"][anchor-column="${column}"]`;
      const seatEl = svgContainerRef.current?.querySelector(`${selectorA}, ${selectorB}`);
      if (seatEl) colorSeatElement(seatEl, OPEN_COLOR);
    });
  }, [forceOpenSeats]);

  return (
    <>
      <div className={cx(container, className)} style={style} {...props}>
        <h3 className={title}>Selecione seus assentos</h3>

        <div className={mapWrapper}>
          {isLoading && (
            <div className={loadingMessage}>
              Carregando mapa de assentos...
            </div>
          )}

          {isError && (
            <div className={errorMessage}>
              Erro ao carregar o mapa de assentos. Tente novamente.
            </div>
          )}

          {svgContent && !isLoading && !isError && (
            <>
              <PinchZoom>
                <div
                  ref={mapContainerRef}
                  onClick={handleSeatClick}
                  onMouseMove={handleMouseMove}
                  style={{
                    width: '100%',
                    height: '100%',
                    cursor: isOverSeat ? 'pointer' : 'default',
                  }}
                >
                  <div ref={svgContainerRef} className={svgContainer} />
                </div>
              </PinchZoom>

              <div className={legendOverlay}>
                <SeatLegend />
              </div>

              {svgProcessing && (
                <div className={loadingOverlay}>
                  <div className={spinner} />
                  <p style={{ marginTop: 16, fontSize: 14 }}>
                    Carregando mapa...
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Ticket Selection Modal */}
      {modalOpen && currentSeat && (
        <div className={modalOverlay} onClick={() => setModalOpen(false)}>
          <div className={modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={modalHeader}>
              <h2 className={modalTitle}>Selecione o Tipo de Ingresso</h2>
              <button className={modalClose} onClick={() => setModalOpen(false)}>
                ×
              </button>
            </div>

            <div className={seatInfo}>
              <p><strong>Zona:</strong> {currentSeat.zone}</p>
              <p><strong>Fileira:</strong> {currentSeat.row}</p>
              <p><strong>Assento:</strong> {currentSeat.column}</p>
            </div>

            {loadingTickets ? (
              <div className={loadingTicketsStyle}>
                <div className={spinner} style={{ margin: '0 auto 16px' }} />
                <p>Carregando ingressos...</p>
              </div>
            ) : availableTickets.length === 0 ? (
              <div className={noTicketsStyle}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ margin: '0 auto 16px', color: '#f59e0b' }}>
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <h4>Não há ingressos disponíveis para esta zona.</h4>
                <p>Por favor, escolha um assento em outra zona ou entre em contato com o suporte.</p>
              </div>
            ) : (
              <div className={ticketList}>
                {availableTickets.map((ticket, index) => {
                  const isSelected = selectedTicketIndex === index;
                  return (
                    <div
                      key={index}
                      className={ticketOption}
                      onClick={() => {
                        setSelectedTicketIndex(index);
                        handleTicketSelect(ticket);
                      }}
                    >
                      <div className={ticketOptionHeader}>
                        <div className={ticketRadio}>
                          <div className={cx(ticketRadioInner, isSelected && ticketRadioInnerSelected)} />
                        </div>
                        <h4 className={ticketName}>
                          {ticket.name === 'Meia' ? 'Meia-entrada' : ticket.name}
                        </h4>
                        <span className={ticketPrice}>
                          {new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL'
                          }).format(ticket.price)}
                        </span>
                      </div>
                      <p className={ticketDescription}>
                        {ticket.seatsPerTicket && ticket.seatsPerTicket > 1
                          ? `Equivale a ${ticket.seatsPerTicket} assentos`
                          : (ticket.name === 'Meia' || ticket.name === 'Meia-entrada')
                          ? 'Desconto de 50% mediante comprovação'
                          : 'Ingresso padrão'}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default CheckoutSeats;
